import React, { useCallback, useState } from 'react'
import classNames from 'classnames'

import { OChat } from './OChat'
import { OMissionDetailsSidebar } from './OMissionDetailsSidebar'
import { OMissionDetailsHeader } from './OMissionDetailsHeader'
import { OMissionCover } from './OMissionCover'
import { OThreadChat } from './OThreadChat'
import { MObjectives } from '../molecules/MObjectives'

export const OMissionDetails = ({
  mission = {},
  thread,
  objectives,
  onUserAction,
  onClose,
  onFinish,
  onExit,
  onProgress,
  onStepUpdated,
  onQuestion,
  onAssistant,
  translations,
  shouldScrollItself = false,
  shouldDisplayCover = true,
  shouldDisplaySidebar = true,
  shouldDisplayCloseIcon = true,
  ...rest
}) => {
  const {
    title = '',
    steps = [],
    messages = [],
    completionMeta = null,
    coverImageUrl,
    coverImageMeta,
  } = mission

  const [displayCover, setDisplayCover] = useState(shouldDisplayCover)
  const [activeStep, setActiveStep] = useState(0)

  const startMission = useCallback(() => {
    setDisplayCover(false)
  }, [])

  const handleOnClose = useCallback(() => {
    if (onClose instanceof Function) {
      onClose()
    }
  }, [onClose])

  const handleStepUpdate = useCallback(() => {
    if (onStepUpdated instanceof Function) {
      onStepUpdated()
    }
  }, [onStepUpdated])

  const updateActiveStep = useCallback(
    newActiveStep => {
      setActiveStep(newActiveStep)
      handleStepUpdate()
    },
    [handleStepUpdate]
  )

  if (!!thread) {
    return (
      <div className="h-full max-w-3xl mx-auto overflow-hidden">
        <OThreadChat thread={thread} {...rest} />
      </div>
    )
  }

  if (shouldScrollItself) {
    return (
      <div className="h-full max-w-3xl mx-auto bg-wildSand overflow-hidden">
        <OChat
          initialMessages={messages}
          completionMeta={completionMeta}
          onUserAction={onUserAction}
          onFinish={onFinish}
          onExit={onExit}
          onProgress={onProgress}
          onQuestion={onQuestion}
          onAssistant={onAssistant}
          shouldScrollItself={shouldScrollItself}
          shouldDisplaySidebar={shouldDisplaySidebar}
          {...rest}
        />
      </div>
    )
  }

  if (displayCover) {
    return (
      <OMissionCover
        title={title}
        steps={steps}
        imageUrl={coverImageUrl}
        imageMeta={coverImageMeta}
        onStart={startMission}
        onClose={handleOnClose}
      />
    )
  }

  return (
    <div
      className={classNames(
        'flex h-full text-codGray bg-wildSand',
        'overflow-hidden transition-all',
        objectives.length > 0 ? 'pt-[36px] lg:pt-0 duration-300' : 'pt-0 duration-300'
      )}
    >
      {shouldDisplaySidebar && (
        <OMissionDetailsSidebar
          title={title}
          imageUrl={coverImageUrl}
          imageMeta={coverImageMeta}
          steps={steps}
          activeStep={activeStep}
          onClose={handleOnClose}
          imageURL={coverImageUrl}
          translations={translations}
          shouldDisplayCloseIcon={shouldDisplayCloseIcon}
          objectives={objectives}
        />
      )}
      <div
        className={classNames('flex-1 max-w-3xl mx-auto', shouldDisplaySidebar && 'pt-16 lg:pt-3')}
      >
        <MObjectives objectives={objectives} />
        <OChat
          initialMessages={messages}
          completionMeta={completionMeta}
          onUserAction={onUserAction}
          onFinish={onFinish}
          onExit={onExit}
          onProgress={onProgress}
          onStepUpdated={updateActiveStep}
          onQuestion={onQuestion}
          onAssistant={onAssistant}
          shouldScrollItself={shouldScrollItself}
          shouldDisplaySidebar={shouldDisplaySidebar}
          {...rest}
        />
      </div>
      <div className="lg:hidden fixed top-0 inset-x-0">
        <OMissionDetailsHeader
          title={title}
          onClose={handleOnClose}
          shouldDisplayCloseIcon={shouldDisplayCloseIcon}
        />
      </div>
    </div>
  )
}
